import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { openQuestionsKeys } from "@/application/query-keys/open-questions.keys";
import {
  openQuestionsRepository,
  type OpenQuestionFilters,
} from "@/infrastructure/repositories/open-questions.supabase.repository";
import type { OpenQuestionRow } from "@/infrastructure/supabase/database.types";

export function useOpenQuestionsList(filters: OpenQuestionFilters = {}) {
  return useQuery({
    queryKey: openQuestionsKeys.list(filters),
    queryFn: () => openQuestionsRepository.list(filters),
  });
}

export function useOpenQuestionDetail(id: string | undefined) {
  return useQuery({
    queryKey: openQuestionsKeys.detail(id ?? ""),
    queryFn: () => openQuestionsRepository.byId(id as string),
    enabled: !!id,
  });
}

export function useCreateOpenQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<OpenQuestionRow, "id" | "created_at" | "updated_at">) =>
      openQuestionsRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: openQuestionsKeys.all });
    },
  });
}

export function useUpdateOpenQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<OpenQuestionRow> }) =>
      openQuestionsRepository.update(id, patch),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: openQuestionsKeys.all });
      queryClient.setQueryData(openQuestionsKeys.detail(data.id), data);
    },
  });
}

export function useDeleteOpenQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => openQuestionsRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: openQuestionsKeys.all });
    },
  });
}
